import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Map, MessageSquare, BarChart3, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import FeedbackWidget from "./FeedbackWidget";
import GoalSwitcher from "./GoalSwitcher";
import ReminderBanner from "./ReminderBanner";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/roadmap", label: "Roadmap", icon: Map, testid: "nav-roadmap" },
  { to: "/interview", label: "Interview", icon: MessageSquare, testid: "nav-interview" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="sticky top-0 z-50 glass border-b border-white/10" style={{ background: "rgba(5,5,5,0.7)" }}>
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" data-testid="nav-logo" className="font-heading font-extrabold text-xl tracking-tighter">
              adyap<span className="text-primary">.</span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {links.map((l) => {
                const active = location.pathname === l.to;
                return (
                  <Link key={l.to} to={l.to} data-testid={l.testid}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                    <l.icon className="w-4 h-4" /> {l.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <GoalSwitcher />
            <div className="text-right">
              <div className="text-sm font-medium leading-tight">{user?.name}</div>
              <div className="text-xs text-zinc-500">{user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <button data-testid="logout-btn" onClick={handleLogout} className="p-2 text-zinc-400 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 px-5 py-3 space-y-1">
            <div className="pb-2 mb-1 border-b border-white/5">
              <GoalSwitcher />
            </div>
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-300 hover:bg-white/5">
                <l.icon className="w-4 h-4" /> {l.label}
              </Link>
            ))}
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-300 w-full">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        )}
      </header>
      <ReminderBanner />
      <main className="max-w-7xl mx-auto px-5 py-8">{children}</main>
      <FeedbackWidget />
    </div>
  );
}
